import type { UpholsteryDesignType } from '@estimator/shared';

/** A catalog row, narrowed to what the dropdown needs. Structural rather than
 *  the shared UpholsteryDesignOption type so all four selectors can pass their
 *  own row shape without a cast. */
export interface DesignCatalogRow {
  key: string;
  name: string;
  sort_order: number;
  /** Optional so a response cached from before this column existed still
   *  renders. Absent is treated as active. */
  active?: boolean;
}

export interface DesignTypeOption {
  key: UpholsteryDesignType;
  label: string;
}

/** `head_roll` -> `Head Roll`. Only reached for a key the catalog does not
 *  name, which happens while the query is still pending, on a failed
 *  request, or if the row backing it was hard-deleted outside the app. */
export function humanizeDesignKey(key: string): string {
  if (!key) return '';
  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Catalog rows -> dropdown options.
 *
 * Two guarantees the selectors depend on:
 *
 * 1. `selected` is always present in the result, labelled from its catalog row
 *    when one exists and from a humanized form of the key otherwise. While the
 *    query is pending, on a failed request, and after a design is deactivated,
 *    the saved design type stays visible and selectable. A pending or failed
 *    request degrades the list to just the selected value.
 * 2. Inactive rows are hidden — unless one of them is the selected value.
 *    Deactivating a design retires it from new work without breaking the
 *    configurations that already use it.
 *
 * There is no longer any filter on a compile-time list of keys. The catalog is
 * the source of truth for which designs exist; filtering against a hardcoded
 * copy of it was exactly what stopped a newly created design from appearing.
 *
 * `selected` is typed as non-nullish, but `design.type` on a component
 * originates in a TEXT column holding JSON rather than from TypeScript, so a
 * nullish value can still arrive here at runtime. Guard against it rather than
 * unmount the app on `undefined.split`.
 */
export function toDesignTypeOptions(
  rows: readonly DesignCatalogRow[] | undefined,
  selected: UpholsteryDesignType,
): DesignTypeOption[] {
  const options: DesignTypeOption[] = [];
  const seen = new Set<string>();

  const ordered = [...(rows ?? [])].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  );

  for (const row of ordered) {
    if (seen.has(row.key) || row.active === false) continue;
    seen.add(row.key);
    options.push({ key: row.key, label: row.name });
  }

  if (selected && !seen.has(selected)) {
    // The selected row may exist but be inactive — look it up (active or not)
    // for its real name before falling back to a humanized guess.
    const selectedRow = (rows ?? []).find((row) => row.key === selected);
    options.unshift({
      key: selected,
      label: selectedRow ? selectedRow.name : humanizeDesignKey(selected),
    });
  }

  return options;
}
